import { useEffect, useState } from "react";
import { X, Loader } from "lucide-react";
import { CategoryForm } from "./CategoryForm";
import { validateCategories } from "../../../utils/categoryValidator";
import AdminCategoryService from "../../../services/admin/AdminCategoryServices";

export function AddCategoryModal({
  newCategories,
  setNewCategories,
  handleImageUpload,
  addAnotherCategory,
  removeCategory,
  setShowAddModal,
  handleAddCategory
}) {
  const [unitMeasurements, setUnitMeasurements] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🔥 Load unit measurements when modal opens
  useEffect(() => {
    let mounted = true;

    const fetchUnits = async () => {
      setLoadingUnits(true);
      try {
        const units = await AdminCategoryService.getUnitMeasurements();
        if (mounted) {
          setUnitMeasurements(units);
        }
      } catch (err) {
        console.error("Failed to load unit measurements");
        if (mounted) {
          setUnitMeasurements(["Custom"]); // fallback (never blocks admin)
        }
      } finally {
        if (mounted) setLoadingUnits(false);
      }
    };

    fetchUnits();

    return () => {
      mounted = false;
    };
  }, []);



  const handleClose = () => {
    if (isSubmitting) return;
    setShowAddModal(false);
    setNewCategories([
      {
        name: "",
        unitMeasurement: "",
        customUnitMeasurement: "",
        image: null,
        imagePreview: null,
      },
    ]);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true); // disable button + show spinner
      await handleAddCategory();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
          <h2 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
            Add Categories
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
          >
            <X size={20} className="text-[#666666] dark:text-[#A0A0A0]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {newCategories.map((category, index) => (
            <CategoryForm
              key={index}
              category={category}
              index={index}
              canRemove={newCategories.length > 1}
              onRemove={() => removeCategory(index)}
              onChange={(field, value) => {
                const updated = [...newCategories];
                updated[index][field] = value;
                setNewCategories(updated);
              }}
              onImageUpload={(file) => handleImageUpload(index, file)}
              unitMeasurements={unitMeasurements}
              disabled={loadingUnits}
            />
          ))}

          <button
            onClick={addAnotherCategory}
            className="w-full py-3 border-2 border-dashed border-[#62397f] dark:border-[#7C3AED] text-[#62397f] dark:text-[#A78BFA] rounded-xl font-semibold text-sm font-inter transition-all hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440] active:scale-95"
          >
            + Add Another Category
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E5E7EB] dark:border-[#2A2A2A]">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white rounded-lg font-medium text-sm font-inter transition-all hover:bg-[#F9FAFB] dark:hover:bg-[#262626] active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2.5 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-lg font-semibold text-sm font-inter transition-all hover:shadow-lg hover:scale-105 active:scale-95"
          >
            {isSubmitting && <Loader className="animate-spin" size={12} />}
            {isSubmitting ? "Adding..." : "Add Categories"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddCategoryModal;
